import type { AttachmentOwnerType } from '@bhops/shared';
import axios from 'axios';
import { apiClient } from './client';

// docs/plan/customer-master-ux/plan.md Phase 4 — 첨부 파일 업로드 client.
//
// 2-step upload (S3 presigned PUT):
//   1. POST /attachments/uploads → 서버가 attachmentId + presigned URL 반환
//   2. PUT presigned URL with file bytes (CORS 직접 S3)
//   3. POST /attachments/:id/confirm → 서버가 head 검증 후 status UPLOADED 으로 전환
//
// 본 client 는 위 3단계를 한 함수 `uploadAttachment()` 로 묶음.

export interface RequestUploadBody {
  ownerType: AttachmentOwnerType;
  ownerId: string;
  contentType: string;
  byteSize: number;
  originalFilename?: string;
}

export interface PresignedPut {
  url: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export interface RequestUploadResponse {
  attachmentId: string;
  presigned: PresignedPut;
}

export interface AttachmentDto {
  id: string;
  contentType: string;
  byteSize: number;
  originalFilename: string | null;
  uploadedAt: string;
  downloadUrl: string;
  // data-lifecycle §3 (PR-5d) — soft-delete 스탬프(삭제됨 배지 + 복구/완전삭제용).
  // includeDeleted 목록(삭제 권한)에서만 non-null.
  deletedAt?: string | null;
}

export interface ConfirmUploadBody {
  checksumSha256?: string;
}

export const attachmentsApi = {
  async requestUpload(body: RequestUploadBody): Promise<RequestUploadResponse> {
    const { data } = await apiClient.post<RequestUploadResponse>('/attachments/uploads', body);
    return data;
  },
  async confirmUpload(attachmentId: string, body: ConfirmUploadBody = {}): Promise<AttachmentDto> {
    const { data } = await apiClient.post<AttachmentDto>(
      `/attachments/${attachmentId}/confirm`,
      body,
    );
    return data;
  },
  async list(
    ownerType: AttachmentOwnerType,
    ownerId: string,
    includeDeleted = false,
  ): Promise<AttachmentDto[]> {
    const { data } = await apiClient.get<AttachmentDto[]>('/attachments', {
      params: { ownerType, ownerId, ...(includeDeleted ? { includeDeleted: true } : {}) },
    });
    return data;
  },
  // data-lifecycle §3/D3 (PR-5d) — 소프트삭제(복구 가능, S3 보존).
  async remove(attachmentId: string): Promise<void> {
    await apiClient.delete(`/attachments/${encodeURIComponent(attachmentId)}`);
  },
  // soft-delete 복구(셀프).
  async restore(attachmentId: string): Promise<void> {
    await apiClient.post(`/attachments/${encodeURIComponent(attachmentId)}/restore`);
  },
  // 셀프 완전삭제(소프트삭제된 첨부만, S3 정리).
  async hardDelete(attachmentId: string): Promise<void> {
    await apiClient.delete(`/attachments/${encodeURIComponent(attachmentId)}/permanent`);
  },
};

/**
 * High-level helper — request → S3 PUT → confirm. 한 파일 업로드를 한 호출로.
 *
 * 주의: S3 PUT 은 별도 axios instance (apiClient 의 baseURL 무시) 로 호출.
 * presigned URL 의 host 가 S3 도메인이므로 baseURL prefix 가 붙으면 안 됨.
 */
export async function uploadAttachment(
  file: File,
  owner: { ownerType: AttachmentOwnerType; ownerId: string },
): Promise<AttachmentDto> {
  const req = await attachmentsApi.requestUpload({
    ownerType: owner.ownerType,
    ownerId: owner.ownerId,
    contentType: file.type || 'application/octet-stream',
    byteSize: file.size,
    originalFilename: file.name,
  });

  await axios.put(req.presigned.url, file, {
    headers: {
      ...req.presigned.headers,
      'Content-Type': file.type || 'application/octet-stream',
    },
  });

  return attachmentsApi.confirmUpload(req.attachmentId);
}
